//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { ContentState } from 'draft-js';

import {
  IGetShowroomShortLinkRequest,
  IGetShowroomShortLinkResponse,
} from '@apis/showroom/showroomApiModel';
import { Props as EmojiPickerProps } from '@components/atoms/emoji-picker/EmojiPicker';
import { IUiaModalClosedResponse } from '@legacy-components/common/universal-image-actions/models/events';
import FacebookPageId from '@models/FacebookPageId';
import InstagramHashtag from '@models/InstagramHashtag';

/**
 * Based off https://github.com/facebook/draft-js/blob/master/src/model/decorators/DraftDecorator.js#L54
 */
export interface IDraftDecoratorComponentProps {
  blockKey: string;
  contentState: ContentState;
  decoratedText: string;
  dir?: unknown;
  end: number;
  entityKey?: string;
  offsetKey: string;
  start: number;
}

export interface ISmartInputRecommendedHashtagsConfiguration {
  isEnabled: boolean;
  recommendations: IRecommendedHashtag[];
}

export interface ISmartInputGenerativeAiCaptionConfiguration {
  isEnabled: boolean;
  isEnabledForPlanTier: boolean;
  isModalTriggerEnabled: boolean;
  getGeneratedCaption: () => Promise<string | undefined>;
  disabledTierTooltipContent?: React.ReactNode;
}

export interface ISmartInputGenerativeAiSocialInboxResponseConfiguration {
  isAiGenerativeResponseLoading: boolean;
  isEnabled: boolean;
  shouldDisableMenu: boolean;
  dropdownConfig: DropdownSection | undefined;
  getGeneratedResponse: (value: DropdownValue) => Promise<string>;
}

export interface ISmartInputEmojiPickerConfiguration {
  isEnabled: boolean;
  placement?: EmojiPickerProps['placement'];
}
export interface ISmartInputMentionsConfiguration {
  getFacebookPageById?: (
    pageId: FacebookPageId,
  ) => Promise<IFacebookPageSuggestion | undefined>;
  getMentionsSuggestions?: (
    query: string,
  ) => Promise<MentionsSuggestion[] | undefined>;
  isEnabled: boolean;
  isInstagramUserEmbedEnabled?: boolean; // if true there is no search dropdown, as this is not currently possible w meta's api
}

export interface ISmartInputLinkShorteningConfiguration {
  isEnabled: boolean;
  getOriginalLink: (shortLink: string) => Promise<string | undefined>;
  getShortLink: (originalLink: string) => Promise<string | undefined>;
  additionalInfo?: React.ReactElement;
  isRevenueTrackingEnabled?: boolean;
}

export interface ISmartInputShowroomLinksConfiguration {
  isEnabled: boolean;
  getShowroomLink: (showroomLinkCallback: ShowroomLinkCallback) => void;
  getShowroomShortlink: (
    req: IGetShowroomShortLinkRequest,
  ) => Promise<IGetShowroomShortLinkResponse>;
}

export enum SmartInputEntityType {
  Emoji = 'Emoji',
  Hashtag = 'Hashtag',
  ShortenedLink = 'ShortenedLink',
}

export interface IRecommendedHashtag {
  value: InstagramHashtag;
  shouldShow: boolean;
}

export type ShowroomLinkCallback = (res: IUiaModalClosedResponse) => void;

export interface ILinkShorteningModalConfiguration {
  initialOriginalLinkValue?: string;
  initialShortLinkValue?: string;
  insertShortLink: (
    shortLink: string,
    options?: {
      shouldPrependSpace: boolean;
      shouldAppendSpace: boolean;
    },
  ) => Promise<void>;
  isEditingLink: boolean;
}

export interface IMentionsSuggestionsQuery {
  blockKey: string;
  end: number;
  queryString: string;
  start: number;
}

export type MentionsSuggestion =
  | IFacebookPageSuggestion
  | ITwitterUserSuggestion;

export enum MentionsSuggestionType {
  FacebookPage = 'FacebookPage',
  TwitterUser = 'TwitterUser',
}

export interface ITwitterUserSuggestion {
  avatarUrl: string;
  isVerified: boolean;
  name: string;
  type: MentionsSuggestionType.TwitterUser;
  username: string;
}

export interface IFacebookPageSuggestion {
  id: FacebookPageId;
  isVerified: boolean;
  link: string;
  name: string;
  type: MentionsSuggestionType.FacebookPage;
}

export enum DropdownSectionType {
  CHECKBOX = 'CHECKBOX',
  RADIO = 'RADIO',
  STANDARD = 'STANDARD',
}

export interface DropdownConfig {
  label: string;
  value: DropdownValue;
  tooltip?: string;
  subOptions?: DropdownConfig[];
  canBeDisabled: boolean;
}

// TODO: PD-256229 - Update with Actual values once BE finalised
export enum DropdownValue {
  GENERATE_REPLY = 'GenerateReply',
  IMPROVE_WRITING = 'ImproveWriting',
  FIX_SPELLING_AND_GRAMMAR = 'FixSpellingAndGrammar',
  MAKE_SHORTER = 'MakeShorter',
  MAKE_LONGER = 'MakeLonger',
  SIMPLIFY_LANGUAGE = 'SimplifyLanguage',
  CHANGE_TONE = 'ChangeTone',
  TONE_PROFESSIONAL = 'Professional',
  TONE_CASUAL = 'Casual',
  TONE_STRAIGHTFORWARD = 'Straightforward',
  TONE_CONFIDENT = 'Confident',
  TONE_FRIENDLY = 'Friendly',
}

export interface DropdownSection {
  label: string;
  type: DropdownSectionType;
  options: DropdownConfig[];
}
